import { Link } from "react-router-dom";
import classNames from "classnames";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolDiscordIcon } from "@ugitgud/legos/ui/icon/SVG/LolDiscordIcon";
import { SvgBrandsBattleNet } from "@ugitgud/legos/ui/icon/SVG/BrandsBattleNet";
import { SvgBrandsFacebook } from "@ugitgud/legos/ui/icon/SVG/BrandsFacebook";
import { SvgBrandsGoogle } from "@ugitgud/legos/ui/icon/SVG/BrandsGoogle";
import { SvgBrandsRiot } from "@ugitgud/legos/ui/icon/SVG/BrandsRiot";

export type OAuthProvider = "google" | "discord" | "facebook" | "custom:riot" | "custom:bnet";

const OAUTH_BUTTONS: Record<OAuthProvider, { label: string; className: string; icon: React.ReactElement }> = {
  "custom:riot": { label: "Riot", className: "bg-[#eb0029]", icon: <SvgBrandsRiot /> },
  "custom:bnet": { label: "Battle.net", className: "bg-[#171920] border border-[#2f3341]", icon: <SvgBrandsBattleNet /> },
  discord: { label: "Discord", className: "bg-[#5865F2]", icon: <SvgLolDiscordIcon /> },
  google: { label: "Google", className: "bg-[#f7f7f7]", icon: <SvgBrandsGoogle /> },
  facebook: { label: "Facebook", className: "bg-[#2374e1]", icon: <SvgBrandsFacebook /> },
};

type LoginFormFooterProps = React.PropsWithChildren<{
  signUpTo?: string;
  recoverTo?: string;
  onRecover?: () => void;
  providers?: OAuthProvider[];
  onOAuth?: (provider: OAuthProvider) => void;
  oauthDisabled?: boolean;
  oauthError?: string;
}>;

export const LoginFormFooter = ({
  signUpTo,
  recoverTo,
  onRecover,
  providers = ["custom:riot", "google", "discord"],
  onOAuth,
  oauthDisabled,
  oauthError,
  children,
}: LoginFormFooterProps) => (
  <>
    <div className="flex items-center my-[20px]">
      <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
      <span className="mx-2 text-[10px] text-lavender-50">OR</span>
      <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
    </div>
    {oauthError && <p className="mb-[8px] text-[14px] text-accent-red-450 font-[500]">{oauthError}</p>}
    <div className="mb-[20px] flex gap-[12px]">
      {providers.map((provider) => (
        <button
          key={provider}
          type="button"
          className={classNames(
            "flex-1 h-[36px] rounded-[4px] cursor-pointer flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed",
            OAUTH_BUTTONS[provider].className,
          )}
          aria-label={`Login with ${OAUTH_BUTTONS[provider].label}`}
          disabled={oauthDisabled}
          onClick={() => onOAuth?.(provider)}
        >
          <Icon icon={OAUTH_BUTTONS[provider].icon} className="w-[20px] h-[20px]" />
        </button>
      ))}
    </div>
    {children}
    <div className="mb-[6px] text-lavender-50 text-[14px] font-inter">
      Don't have an account?{" "}
      {signUpTo ? (
        <Link className="underline text-accent-blue-400 font-bold" to={signUpTo}>
          Join now
        </Link>
      ) : (
        <span className="underline text-accent-blue-400 font-bold">Join now</span>
      )}
    </div>
    {recoverTo ? (
      <Link className="text-[14px] text-accent-blue-400 font-bold underline" to={recoverTo}>
        Trouble logging in?
      </Link>
    ) : onRecover ? (
      <button type="button" className="text-[14px] text-accent-blue-400 font-bold underline" onClick={onRecover}>
        Trouble logging in?
      </button>
    ) : (
      <span className="text-[14px] text-accent-blue-400 font-bold underline">Trouble logging in?</span>
    )}
  </>
);
